import Bottombar from "@/components/bottombar"
import Button from "@/components/button"
import Topbar from "@/components/topbar"
import { posthog } from "@/lib/posthog"
import AddTruckForm from "@/rmpm/components/add-truck-form"
import { handleNetworkError } from "@/services/base"
import {
  createRMPMProductSheet,
  createRMPMSheet,
  loadSchemas,
  processResample,
  RMPMSheetType,
  startRMPMProcess,
} from "@/services/rmpm"
import useStore from "@/store"
import { PMTruck, RMTruck } from "@/store/rmpm"
import { HTTPError } from "ky"
import { useState } from "react"
import { useNavigate } from "react-router"
import { toast } from "sonner"

export default function OnboardRMPMPage() {
  const navigate = useNavigate()
  const userStore = useStore.use.user()
  const [rmTrucks, setRMTrucks] = useState<RMTruck[]>([])
  const [pmTrucks, setPMTrucks] = useState<PMTruck[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const sessionStore = useStore.use.session()
  const rmpmStore = useStore.use.rmpm()

  function canStart() {
    if (!(rmTrucks.length || pmTrucks.length)) {
      return false
    }

    for (const truck of rmTrucks) {
      const valid = isValidTruck(truck)
      if (!valid) {
        return false
      }
    }

    for (const truck of pmTrucks) {
      const valid = isValidTruck(truck)
      if (!valid) {
        return false
      }
    }

    return true
  }

  function isValidTruck(truck: RMTruck | PMTruck): boolean {
    if (!truck.truckNumber) {
      return false
    }

    if (!truck.products.length) {
      return false
    }

    return true
  }

  async function handleStartProcess() {
    setIsLoading(true)

    try {
      // 1. Start the RMPM process first
      const processData = await startRMPMProcess({
        processId: rmpmStore.process.processId,
        shiftSessionId: sessionStore.session.id,
      }) as { employees?: any[], handover?: any[], handovers?: any[] };

      // Update session + employees
      sessionStore.startProcess(rmpmStore.process.processId);
      rmpmStore.setEmployees(processData.employees ?? []);

      const handoverItems = processData.handovers || processData.handover || [];
      processResample(handoverItems)

      // 2. Create sheets on server for each RM truck
      const rmTrucksWithSheetIds: RMTruck[] = []
      for (const truck of rmTrucks) {
        if (!truck.truckNumber) continue

        try {
          const response = await createRMPMSheet({
            truckNumber: truck.truckNumber,
            truckType: "RM" as RMPMSheetType,
          })

          // Create product sheets for each product in this truck
          const productSheetIds: Record<string, string> = {}
          for (const productId of truck.products) {
            const productDetails = rmpmStore.allProducts.rmProducts.find(p => p.id === productId)
            if (productDetails) {
              try {
                const productResponse = await createRMPMProductSheet(response.sheetId, {
                  productId: productDetails.id,
                  productName: productDetails.name,
                })
                productSheetIds[productId] = productResponse.productSheetId
              } catch (err) {
                console.error(`Failed to create product sheet for ${productDetails.name}:`, err)
              }
            }
          }

          rmTrucksWithSheetIds.push({
            ...truck,
            sheetId: response.sheetId,
            // Store productSheetIds temporarily - they'll be applied in loadSchemas
            productSheetIds,
          } as RMTruck & { productSheetIds: Record<string, string> })
        } catch (err) {
          console.error(`Failed to create sheet for RM truck ${truck.truckNumber}:`, err)
          const handled = await handleNetworkError(err, navigate)
          if (!handled && err instanceof HTTPError) {
            toast.error(`Failed to create sheet for truck ${truck.truckNumber}`)
          }
        }
      }

      // 3. Create sheets on server for each PM truck
      const pmTrucksWithSheetIds: PMTruck[] = []
      for (const truck of pmTrucks) {
        if (!truck.truckNumber) continue

        const pmSheetType: RMPMSheetType = truck.pmType === "LAMINATE" ? "PM_LAMINATE" : "PM_CARTON"

        try {
          const response = await createRMPMSheet({
            truckNumber: truck.truckNumber,
            truckType: pmSheetType,
          })

          // Create product sheets for each product in this truck
          const productSheetIds: Record<string, string> = {}
          for (const productId of truck.products) {
            const productDetails = rmpmStore.allProducts.pmProducts.find(p => p.id === productId)
            if (productDetails) {
              try {
                const productResponse = await createRMPMProductSheet(response.sheetId, {
                  productId: productDetails.id,
                  productName: productDetails.name,
                  market: 'DOMESTIC',  // Default market for PM products
                })
                productSheetIds[productId] = productResponse.productSheetId
              } catch (err) {
                console.error(`Failed to create product sheet for ${productDetails.name}:`, err)
              }
            }
          }

          pmTrucksWithSheetIds.push({
            ...truck,
            sheetId: response.sheetId,
            // Store productSheetIds temporarily - they'll be applied in loadSchemas
            productSheetIds,
          } as PMTruck & { productSheetIds: Record<string, string> })
        } catch (err) {
          console.error(`Failed to create sheet for PM truck ${truck.truckNumber}:`, err)
          const handled = await handleNetworkError(err, navigate)
          if (!handled && err instanceof HTTPError) {
            toast.error(`Failed to create sheet for truck ${truck.truckNumber}`)
          }
        }
      }

      // 4. Load schemas with trucks that have server-generated sheetIds
      await loadSchemas(rmTrucksWithSheetIds, pmTrucksWithSheetIds)
      await navigate("/rm-pm")

    } catch (err) {
      posthog.captureException(err);

      const handled = await handleNetworkError(err, navigate);
      if (!handled && err instanceof HTTPError) {
        toast.error(err.message);
      }
    } finally {
      setIsLoading(false)
    }
  }

  async function handleClickBack() {
    await navigate("/manage-processes")
  }

  return (
    <div className="relative h-screen overflow-auto">
      <Topbar
        className="sticky top-0 left-0 z-50"
        title="RM/PM"
        subtitle={`${userStore.employee.name} (${userStore.plant.name})`}
        showBack
        onClickBack={handleClickBack}
      ></Topbar>

      <div className="pb-10 min-h-250">
        <AddTruckForm
          pmTrucks={pmTrucks}
          rmTrucks={rmTrucks}
          defaultPmTrucks={[]}
          defaultRmTrucks={[]}
          onChangePmTrucks={setPMTrucks}
          onChangeRmTrucks={setRMTrucks}
        />
      </div>

      <Bottombar className="sticky flex items-center gap-4 bottom-0 z-50">
        <Button variant="outline" onClick={handleClickBack} disabled={isLoading}>
          Back
        </Button>

        <Button
          className="grow"
          onClick={handleStartProcess}
          disabled={!canStart() || isLoading}
        >
          {isLoading ? "Starting..." : "Start"}
        </Button>
      </Bottombar>
    </div>
  )
}
